import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { XpoSnackBar } from '@xpo-ltl/ngx-ltl-core/snack-bar';
import { RatingAccessorialDetail, RatingAmcDetail } from '@xpo-ltl/sdk-dynamicpricing';
import { Employee, EmployeeName } from '@xpo-ltl/sdk-humanresource';
import moment from 'moment';
import { SnackBarMessageComponent } from '../shared/components/snack-bar-message/snack-bar-message.component';

export const FREE_TEXT_PATTERN_STRING = '[a-zA-Z0-9 .,\\/?$+@%&()\\-*:\'"=!><\\n]*';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  constructor(public snackbar: XpoSnackBar) {}

  // ── Static helpers ────────────────────────────────────────────────────────

  /**
   * Returns the maximum value of a sequence-number field across a flat list of items.
   * @param items  Array of objects
   * @param field  Name of the numeric sequence field (e.g. 'amcOverrideSequenceNbr')
   */
  static getMaxSequenceNumber(items: any[], field: string): number {
    const numbers = items.map((item) => item[field]).filter((n) => n != null);
    return numbers.length ? Math.max(...numbers) : 0;
  }

  /**
   * Returns true if the value is a finite, parseable number.
   */
  static isNumber(n: any): boolean {
    return !isNaN(n) && !isNaN(parseFloat(n));
  }

  /**
   * Runs the shared override validation loop.
   * @param list        Flat list of override items to validate
   * @param rowCheck    Per-row validation — return true if the row is VALID
   * @param errorMsg    The current error message map
   */
  static isValidOverride(list: any[], rowCheck: (item: any) => boolean, errorMsg: Record<string, any>): any {
    let valid = null;
    list.forEach((item) => {
      valid = rowCheck(item) ? null : ' ';
    });
    Object.keys(errorMsg).forEach((d) => {
      if (errorMsg[d]) {
        valid = valid ? valid : '';
        valid += ` ${d.replace(/[0-9]/g, '')} ${errorMsg[d]} `;
      }
    });
    return valid;
  }

  static FreeTextValidator(control: FormControl): { [key: string]: boolean } | null {
    // Allowed:
    // Alphanumeric characters: a-z, A-Z, 0-9
    // Special characters: . , $ / \ + - & ( ) @ * % < > “ ' : ! = ?
    // Spaces and new lines.
    if (control.value && typeof control.value === 'string') {
      const isTextInvalid = HelperService.validateText(control.value);
      if (isTextInvalid) {
        return { invalidComment: true };
      }
      // const normalized = HelperService.normalizeFreeText(control.value);
      // //const commentRegExp: RegExp = /^[a-zA-Z0-9 .,\/?$+@%&()\-*:'"=!><\n]*$/;
      // if (control.value && !commentRegExp.test(normalized)) {
      //   return { invalidComment: true };
      // }
    }
  }

  static validateText(text: string): boolean {
    const normalized = HelperService.normalizeFreeText(text);
    // const commentRegExp: RegExp = /^[a-zA-Z0-9 .,\/?$+@%&()\-*:'"=!><\n]*$/;
    const commentRegExp: RegExp = new RegExp(`^${FREE_TEXT_PATTERN_STRING}$`);
    return text && !commentRegExp.test(normalized);
  }

  static normalizeFreeText(text: string): string {
    return text
      .replace(/\u00A0/g, ' ') // Replace non-breaking spaces with regular spaces
      .replace(/[\r\n]{3,}/g, '\n\n') // Reduce 3+ line breaks to exactly 2 (preserve paragraph spacing)
      .trim(); // Trim leading/trailing whitespace
  }

  // ── Validators ────────────────────────────────────────────────────────────

  requireAtleastOneCheckboxValidator(minRequired = 1): ValidatorFn {
    return function validate(formGroup: UntypedFormGroup) {
      let checked = 0;
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.controls[key];
        if (control.value === true) {
          checked++;
        }
      });
      if (checked < minRequired) {
        return { requireOneCheckboxToBeChecked: true };
      }
      return null;
    };
  }

  requireMatch(control: AbstractControl): { [key: string]: boolean } | null {
    const selection: any = control.value;
    if (isNaN(selection)) {
      return { incorrect: true };
    }
    return null;
  }

  thresholdPeriodEmptyValidator(previousValue: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (previousValue && !control.value) {
        return { thresholdPeriodEmpty: true };
      }
      return null;
    };
  }

  conditionalValidator(predicate: () => boolean, validator: ValidatorFn): ValidatorFn {
    return (formControl: AbstractControl) => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return validator(formControl);
      }
      return null;
    };
  }

  dateGreaterThanOrEqualToValidator(date: moment.Moment): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value) {
        const diff = moment(control.value)
          .startOf('day')
          .diff(date);
        if (diff < 0) {
          return { dateGraterThanOrEqualTo: true };
        }
      }
      return null;
    };
  }

  /**
   * Group-level validator: only fires when both dates are present and individually valid.
   * Sets `expiryNotAfterEffective` when expiry is on or before effective (compared by day).
   */
  expiryAfterEffectiveValidator(effectiveKey = 'effectiveDate', expiryKey = 'expiryDate'): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const effective = group.get(effectiveKey);
      const expiry = group.get(expiryKey);
      if (!effective?.value || !expiry?.value || effective.invalid || expiry.invalid) {
        return null;
      }
      return moment(expiry.value)
        .startOf('day')
        .isSameOrBefore(moment(effective.value).startOf('day'))
        ? { expiryNotAfterEffective: true }
        : null;
    };
  }

  isNumeric(val: any): boolean {
    return !Array.isArray(val) && !isNaN(parseFloat(val)) && isFinite(val);
  }

  // ── Snackbar helpers ──────────────────────────────────────────────────────

  showCustomErrorSnackBar(detailedMsgData: string[], message: string) {
    return this.snackbar.open({
      message: message,
      detailedMessageComponent: SnackBarMessageComponent,
      detailedMessageComponentData: detailedMsgData,
      status: 'error',
      matConfig: {
        duration: 0,
      },
    });
  }

  getDialogTitle(errorList: string[], message) {
    return errorList.length > 1 ? ` Error List : ${message}` : ` Error : ${message}`;
  }

  showErrorSnackbar(error, actionMessage: string) {
    const errorList = this.getError(error);
    this.snackbar.open({
      message: actionMessage + this.getDialogTitle(errorList, error.error.message),
      detailedMessage: errorList.toString(),
      status: 'error',
      matConfig: {
        duration: 5000,
      },
    });
  }

  showAlertSnackbar(message: string) {
    this.snackbar.open({
      message,
      status: 'error',
      matConfig: { duration: 5000, verticalPosition: 'top' },
    });
  }

  getError(data): string[] {
    const errorList: string[] = [];
    if (data && data.error && data.error.moreInfo) {
      data.error.moreInfo.forEach((element) => {
        errorList.push(`${element.message}`);
      });
    } else if (data && data.message) {
      errorList.push(data.message);
    } else {
      errorList.push('Unexpected error');
    }
    return errorList;
  }

  isExpired(expiryDate: Date | string): boolean {
    const now = moment();
    return now.isAfter(moment(expiryDate));
  }

  isFuture(effectiveDate: Date | string): boolean {
    const now = moment();
    return now.isBefore(moment(effectiveDate));
  }

  getStatusIndicator(
    data: RatingAccessorialDetail | RatingAmcDetail
  ): { statusText: string; statusColor: string; isLabel: boolean } {
    if (this.isExpired(data?.expiryDate)) {
      return { statusText: 'Expired', statusColor: 'darkgrey', isLabel: true };
    }

    if (this.isFuture(data?.effectiveDate)) {
      return { statusText: 'Future', statusColor: 'blue', isLabel: true };
    }

    return { statusText: 'Active', statusColor: 'green', isLabel: true };
  }

  getFullName(employeeDetails: Employee | EmployeeName): string {
    if (!employeeDetails) {
      return '';
    }
    const e =
      'basicInfo' in employeeDetails ? (employeeDetails as Employee).basicInfo : (employeeDetails as EmployeeName);
    return `${e?.firstName ?? ''} ${e?.lastName ?? ''}`.trim();
  }
}
